<script setup lang="ts" generic="T extends string | number | null">
    import { LoaderCircle } from 'lucide-vue-next';
    import type { ControlEmits, ControlProps } from '@/shared/forms';
    import { Field } from '@/shared/ui/molecules/Field';
    import type { InputNativeAttributes } from './types';

    const props = withDefaults(
        defineProps<
            Omit<ControlProps<T>, 'modelValue'> & {
                type?: 'text' | 'number' | 'tel' | 'password' | 'email' | 'url' | 'search' | 'date';
                label?: string;
                description?: string;
                hint?: string;
                hintLabel?: string;
                /** One error message, for plain `v-model` use */
                error?: string;
                required?: boolean;
                /** Shows a spinner after the control and disables it */
                loading?: boolean;
                id?: string;
                /** Attributes for the box around the input and its addons: a Popover's `anchorAttrs`, a `focusout` listener for the whole group */
                boxAttrs?: Record<string, unknown>;
            } & /* @vue-ignore */ InputNativeAttributes
        >(),
        {
            type: 'text',
            disabled: false,
            readonly: false,
            invalid: false,
            errors: () => [],
            label: undefined,
            description: undefined,
            hint: undefined,
            hintLabel: undefined,
            error: undefined,
            required: false,
            loading: false,
            id: undefined,
            boxAttrs: undefined,
        },
    );

    const emit = defineEmits<Pick<ControlEmits<T>, 'blur'>>();

    defineOptions({ inheritAttrs: false });

    const slots = defineSlots<{
        prefix?(): unknown;
        suffix?(): unknown;
        label?(): unknown;
        description?(): unknown;
        hint?(): unknown;
    }>();

    /* An empty `type="number"` input writes `null`, not `''`. */
    const model = defineModel<T>({
        required: true,
        set: (value) => (props.type === 'number' && value === '' ? (null as T) : value),
    });

    const attrs = useAttrs();
    /* `class` and `style` lay out the whole field; every other attribute goes to the `<input>`. */
    const inputAttrs = computed(() => {
        const { class: _class, style: _style, ...rest } = attrs;
        return rest;
    });

    const inputRef = useTemplateRef('input');

    /* A click on the prefix or suffix focuses the input, unless it lands on a button there. */
    function focusInput(event: MouseEvent) {
        if ((event.target as HTMLElement).closest('button')) return;
        inputRef.value?.focus();
    }

    defineExpose({ focus: () => inputRef.value?.focus(), input: inputRef });
</script>

<template>
    <Field
        :id="id"
        :class="attrs.class"
        :style="attrs.style"
        :label="label"
        :description="description"
        :hint="hint"
        :hint-label="hintLabel"
        :error="error"
        :errors="errors"
        :invalid="invalid"
        :required="required"
        :disabled="disabled || loading"
    >
        <template
            v-if="slots.label"
            #label
        >
            <slot name="label" />
        </template>
        <template
            v-if="slots.hint"
            #hint
        >
            <slot name="hint" />
        </template>
        <template
            v-if="slots.description"
            #description
        >
            <slot name="description" />
        </template>

        <template #default="field">
            <div
                v-bind="boxAttrs"
                data-slot="input"
                class="ui-input"
            >
                <div
                    v-if="slots.prefix"
                    class="ui-input__addon ui-input__addon--prefix"
                    @click="focusInput"
                >
                    <slot name="prefix" />
                </div>
                <input
                    v-bind="inputAttrs"
                    :id="field.id"
                    ref="input"
                    v-model="model"
                    class="ui-input__control"
                    :type="type"
                    :disabled="disabled || loading"
                    :readonly="readonly"
                    :aria-required="required || undefined"
                    :aria-invalid="field.invalid || undefined"
                    :aria-describedby="field.describedBy"
                    :aria-busy="loading || undefined"
                    @blur="emit('blur')"
                />
                <div
                    v-if="slots.suffix || loading"
                    class="ui-input__addon ui-input__addon--suffix"
                    @click="focusInput"
                >
                    <LoaderCircle
                        v-if="loading"
                        class="ui-input__spinner"
                        aria-hidden="true"
                    />
                    <slot name="suffix" />
                </div>
            </div>
        </template>
    </Field>
</template>

<style scoped>
    /*
      Below `utilities` so consumers' Tailwind classes still override these defaults, above Bulma's `base`.
      The box (`.ui-input`) draws the border, the focus ring and the invalid border; the `<input>` inside it is bare.
    */
    @layer components {
        .ui-input {
            position: relative;
            display: flex;
            align-items: center;
            width: 100%;
            min-width: 0;
            height: 48px;
            padding: 16px;
            border: 1px solid var(--color-input);
            border-radius: 12px;
            background-color: var(--color-background-primary);
            box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
            transition:
                color 150ms cubic-bezier(0.4, 0, 0.2, 1),
                box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        .ui-input:has(.ui-input__control:focus-visible) {
            border-color: var(--color-ring);
            box-shadow:
                0 0 0 2px var(--color-border-highlighted),
                0 1px 2px 0 rgb(0 0 0 / 0.05);
        }

        .ui-input:has(.ui-input__control[aria-invalid='true']) {
            border-color: var(--color-destructive);
        }

        .ui-input:has(.ui-input__control[aria-invalid='true']:focus-visible) {
            box-shadow:
                0 0 0 2px color-mix(in oklab, var(--color-destructive) 20%, transparent),
                0 1px 2px 0 rgb(0 0 0 / 0.05);
        }

        .ui-input:has(.ui-input__control:read-only:not(:disabled)) {
            background-color: var(--color-background-secondary);
        }

        .ui-input__control {
            flex: 1 1 0%;
            width: 100%;
            min-width: 0;
            height: 48px;
            padding: 12px 0;
            border: 0;
            border-radius: 0;
            background-color: transparent;
            color: inherit;
            font: inherit;
            font-size: var(--text-base);
            line-height: var(--text-base--line-height);
            outline: none;
        }

        .ui-input__control::placeholder {
            color: var(--color-muted-foreground);
        }

        .ui-input__control::selection {
            background-color: var(--color-primary);
            color: var(--color-primary-foreground);
        }

        .ui-input__control:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
        }

        .ui-input__addon {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding-block: 6px;
            color: var(--color-muted-foreground);
            font-weight: var(--font-weight-medium);
            white-space: nowrap;
            cursor: text;
            user-select: none;
        }

        .ui-input:has(.ui-input__control:disabled) > .ui-input__addon {
            opacity: 0.5;
        }

        .ui-input__addon :deep(svg:not([class*='size-'])) {
            width: 20px;
            height: 20px;
        }

        .ui-input__addon--prefix {
            padding-inline-end: 8px;
        }

        .ui-input__addon--prefix:has(> button) {
            margin-inline-start: -0.45rem;
        }

        .ui-input__addon--suffix:has(> button) {
            margin-inline-end: -0.45rem;
        }

        .ui-input__spinner {
            animation: ui-input-spin 1s linear infinite;
        }

        @media (prefers-reduced-motion: reduce) {
            .ui-input__spinner {
                animation-duration: 3s;
            }
        }

        @keyframes ui-input-spin {
            to {
                transform: rotate(360deg);
            }
        }
    }
</style>
